import React from 'react';

// Mental Health Result Display
export const MentalHealthResult = ({ data }) => {
  if (!data) return null;
  
  return (
    <div className="mt-6 p-6 bg-indigo-50 rounded-lg border-2 border-indigo-200">
      <h3 className="font-bold text-2xl text-indigo-700 mb-4">Mental Wellness Analysis</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow">
          <span className="text-gray-700 font-medium">Mood Score</span>
          <span className={`text-2xl font-bold ${
            data.mood_score >= 7 ? 'text-green-600' :
            data.mood_score >= 4 ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {data.mood_score}/10
          </span>
        </div>
        
        {data.analysis && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-indigo-600 mb-2">Analysis</h4>
            <p className="text-gray-700 leading-relaxed">{data.analysis}</p>
          </div>
        )}
        
        {data.recommendations?.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-indigo-600 mb-2">Recommendations</h4>
            <ul className="space-y-2">
              {data.recommendations.map((rec, idx) => (
                <li key={`mental-rec-${idx}`} className="flex items-start gap-2 text-gray-700">
                  <span className="text-indigo-500 mt-1">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.crisis_indicators && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700 font-semibold">
              Crisis Support: If you're in crisis, please contact a mental health professional or helpline.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Nutrition Result Display
export const NutritionResult = ({ data }) => {
  if (!data) return null;
  
  return (
    <div className="mt-6 p-6 bg-green-50 rounded-lg border-2 border-green-200">
      <h3 className="font-bold text-2xl text-green-700 mb-4">7-Day Meal Plan Generated</h3>
      
      <div className="space-y-3">
        {data.weekly_meal_plan?.map((day, i) => (
          <div key={`meal-day-${day.day || i}`} className="bg-white p-4 rounded shadow">
            <p className="font-bold text-green-600 mb-3 text-lg capitalize">{day.day || `Day ${i+1}`}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <MealCard meal={day.breakfast} icon="🍳" label="Breakfast" color="green" />
              <MealCard meal={day.lunch} icon="🍱" label="Lunch" color="blue" />
              <MealCard meal={day.dinner} icon="🍽️" label="Dinner" color="orange" />
              <MealCard meal={day.snacks} icon="🥤" label="Snacks" color="purple" />
            </div>
          </div>
        ))}
      </div>
      
      {data.nutritional_insights && (
        <div className="bg-white p-4 rounded mt-4 shadow">
          <h4 className="font-semibold text-green-600 mb-2">Nutritional Insights</h4>
          <p className="text-gray-700">{data.nutritional_insights}</p>
        </div>
      )}
    </div>
  );
};

const MealCard = ({ meal, icon, label, color }) => {
  const colorClasses = {
    green: 'border-green-500 text-green-600',
    blue: 'border-blue-500 text-blue-600',
    orange: 'border-orange-500 text-orange-600',
    purple: 'border-purple-500 text-purple-600'
  };
  
  return (
    <div className={`border-l-4 ${colorClasses[color].split(' ')[0]} pl-3`}>
      <p className="font-semibold text-gray-700 flex items-center gap-2">
        <span className="text-xl">{icon}</span> {label}
      </p>
      <p className="text-gray-800 font-medium">{meal?.name || 'Not specified'}</p>
      {meal?.nutritional_info?.calories_per_serving && (
        <p className={`text-xs font-semibold ${colorClasses[color].split(' ')[1]}`}>
          {meal.nutritional_info.calories_per_serving} cal
        </p>
      )}
    </div>
  );
};

// Heart Analysis Result Display
export const HeartResult = ({ data }) => {
  if (!data) return null;
  
  return (
    <div className="mt-6 p-6 bg-red-50 rounded-lg border-2 border-red-200">
      <h3 className="font-bold text-2xl text-red-700 mb-4">Heart Health Analysis</h3>
      
      <div className="space-y-4">
        {data.risk_level && (
          <div className={`p-4 rounded-lg ${
            data.risk_level === 'low' ? 'bg-green-100' :
            data.risk_level === 'moderate' ? 'bg-yellow-100' : 'bg-red-100'
          }`}>
            <p className="font-semibold">Risk Level: <span className="capitalize">{data.risk_level}</span></p>
          </div>
        )}
        
        {data.summary && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-red-600 mb-2">Summary</h4>
            <p className="text-gray-700">{data.summary}</p>
          </div>
        )}
        
        {data.recommendations?.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-red-600 mb-2">Recommendations</h4>
            <ul className="space-y-2">
              {data.recommendations.map((rec, idx) => (
                <li key={`heart-rec-${idx}`} className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 mt-1">❤️</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Skin Scanner Result Display
export const SkinScannerResult = ({ data }) => {
  if (!data) return null;
  
  return (
    <div className="mt-6 p-6 bg-orange-50 rounded-lg border-2 border-orange-200">
      <h3 className="font-bold text-2xl text-orange-700 mb-4">Skin Analysis Results</h3>
      
      <div className="space-y-4">
        {data.condition && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-orange-600 mb-2">Detected Condition</h4>
            <p className="text-xl font-bold text-gray-800">{data.condition}</p>
            {data.confidence && (
              <p className="text-sm text-gray-500 mt-1">
                Confidence: {Math.round(data.confidence * 100)}%
              </p>
            )}
          </div>
        )}
        
        {data.description && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-orange-600 mb-2">Description</h4>
            <p className="text-gray-700">{data.description}</p>
          </div>
        )}
        
        {data.recommendations?.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-orange-600 mb-2">Recommendations</h4>
            <ul className="space-y-2">
              {data.recommendations.map((rec, idx) => (
                <li key={`skin-rec-${idx}`} className="flex items-start gap-2 text-gray-700">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
          <p className="text-yellow-800 text-sm">
            <strong>Disclaimer:</strong> This AI analysis is for informational purposes only and should not replace professional medical advice.
          </p>
        </div>
      </div>
    </div>
  );
};

// Symptom Checker Result Display  
export const SymptomResult = ({ data }) => {
  if (!data) return null;
  
  return (
    <div className="mt-6 p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
      <h3 className="font-bold text-2xl text-blue-700 mb-4">Symptom Analysis</h3>
      
      <div className="space-y-4">
        {data.assessment && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-blue-600 mb-2">Assessment</h4>
            <p className="text-gray-700">{data.assessment}</p>
          </div>
        )}
        
        {data.possible_conditions?.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-blue-600 mb-2">Possible Conditions</h4>
            <ul className="space-y-2">
              {data.possible_conditions.map((condition, idx) => (
                <li key={`condition-${idx}`} className="flex items-start gap-2 text-gray-700">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>{condition}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {data.urgency && (
          <div className={`p-4 rounded-lg ${
            data.urgency === 'low' ? 'bg-green-100 text-green-800' :
            data.urgency === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
            'bg-red-100 text-red-800'
          }`}>
            <p className="font-semibold">Urgency Level: <span className="capitalize">{data.urgency}</span></p>
          </div>
        )}
        
        {data.recommendations?.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow">
            <h4 className="font-semibold text-blue-600 mb-2">Recommendations</h4>
            <ul className="space-y-2">
              {data.recommendations.map((rec, idx) => (
                <li key={`symptom-rec-${idx}`} className="flex items-start gap-2 text-gray-700">
                  <span className="text-blue-500 mt-1">✓</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Unified result display component
export const ResultDisplays = ({ result }) => {
  if (!result) return null;
  switch (result.type) {
    case 'mental-health': return <MentalHealthResult data={result.data} />;
    case 'nutrition': return <NutritionResult data={result.data} />;
    case 'heart': return <HeartResult data={result.data} />;
    case 'skin': return <SkinScannerResult data={result.data} />;
    case 'symptom': return <SymptomResult data={result.data} />;
    default: return null;
  }
};

export default {
  MentalHealthResult,
  NutritionResult,
  HeartResult,
  SkinScannerResult,
  SymptomResult
};
